import React from 'react';
import { getResourceColor, getInitials } from '../../../../../utils/resourceUtils';

/**
 * ResourceAllocation Component
 * Handles the display and management of assigned workers and vehicles within an assignment.
 * Supports grouping by kolonne and conflict visualization.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isReadOnly - Whether the component is in view-only mode
 * @param {Object} props.form - Current form state (contains workers/vehicles)
 * @param {Function} props.handleClearAll - Clears all assigned resources
 * @param {Function} props.setIsResModalOpen - Opens the resource selection modal
 * @param {Function} props.handleDrop - Handles drop events for resources
 * @param {Function} props.handleDragOver - Handles drag over events
 * @param {Function} props.handleDragLeave - Handles drag leave events
 * @param {Array} props.kolonnen - List of all kolonnen
 * @param {Object} props.conflictSummary - Aggregated conflict data
 * @param {boolean} props.isConflictDetectionEnabled - Whether conflict detection is active
 * @param {Function} props.handleConflictEnter - Triggered on conflict hover
 * @param {Function} props.handleConflictLeave - Triggered on conflict hover leave
 * @param {Function} props.removeKolonne - Removes an entire kolonne from the assignment
 * @param {Object} props.liveConflicts - Real-time conflict data for individual resources
 * @param {Function} props.removeWorker - Removes a worker
 * @param {Array} props.vehicles - List of all vehicles (for lookups)
 * @param {Function} props.toggleVehicle - Toggles a vehicle's assignment
 */
const ResourceAllocation = ({
    isReadOnly,
    form,
    handleClearAll,
    setIsResModalOpen,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    kolonnen,
    conflictSummary,
    isConflictDetectionEnabled,
    handleConflictEnter,
    handleConflictLeave,
    removeKolonne,
    liveConflicts,
    removeWorker,
    vehicles,
    toggleVehicle
}) => {
    // Group resources by kolonne_id
    const grouped = {};
    const independentWorkers = [];
    const independentVehicles = [];

    form.workers.forEach(w => {
        if (w.kolonne_id && String(w.kolonne_id) !== 'null') {
            if (!grouped[w.kolonne_id]) grouped[w.kolonne_id] = { workers: [], vehicles: [] };
            grouped[w.kolonne_id].workers.push(w);
        } else {
            independentWorkers.push(w);
        }
    });

    form.vehicles.forEach(v => {
        if (v.kolonne_id && String(v.kolonne_id) !== 'null') {
            if (!grouped[v.kolonne_id]) grouped[v.kolonne_id] = { workers: [], vehicles: [] };
            grouped[v.kolonne_id].vehicles.push(v);
        } else {
            independentVehicles.push(v);
        }
    });

    return (
        <div className="resource-section">
            <div className="section-header-row">
                <label>👥 Mitarbeiter & 🚛 Fahrzeuge</label>
                {!isReadOnly && (form.workers.length > 0 || form.vehicles.length > 0) && (
                    <button type="button" className="btn-clear-all" onClick={handleClearAll}>
                        Alle löschen 🗑️
                    </button>
                )}
            </div>
            <div 
                className={`drop-zone ${!isReadOnly ? 'clickable' : ''}`} 
                onClick={() => !isReadOnly && setIsResModalOpen(true)} 
                onDrop={!isReadOnly ? handleDrop : undefined} 
                onDragOver={!isReadOnly ? handleDragOver : undefined} 
                onDragLeave={!isReadOnly ? handleDragLeave : undefined}
            >
                {form.workers.length === 0 && form.vehicles.length === 0 ? (
                    <div className="empty-drop">
                        {isReadOnly ? 'Keine Zuweisungen' : 'Klicken zum Auswählen'} <span className="drop-hint">{!isReadOnly && '(oder hierher ziehen)'}</span>
                    </div>
                ) : (
                    <div className="assigned-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="res-grouped-list">
                            {/* Kolonnen Groups */}
                            {Object.keys(grouped).map(kId => {
                                const kData = (kolonnen || []).find(k => String(k.id) === String(kId));
                                const kolonneShared = conflictSummary.kolonnen[kId];
                                
                                return (
                                    <div key={`k-${kId}`} className="kolonne-group" style={{ marginBottom: '12px', borderLeft: '3px solid #3b82f6', background: 'rgba(59, 130, 246, 0.08)', borderRadius: '6px', overflow: 'hidden' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 12px' }}>
                                                <div 
                                                    style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: '8px', 
                                                        fontWeight: 700, 
                                                        fontSize: '0.75rem', 
                                                        color: '#64748b', 
                                                        textTransform: 'uppercase', 
                                                        letterSpacing: '0.05em',
                                                        cursor: (isConflictDetectionEnabled && kolonneShared && kolonneShared.size > 0) ? 'pointer' : 'default'
                                                    }}
                                                    onMouseEnter={(e) => isConflictDetectionEnabled && kolonneShared && kolonneShared.size > 0 && handleConflictEnter(e, Array.from(kolonneShared), kData?.name || 'Kolonne')}
                                                    onMouseLeave={handleConflictLeave}
                                                >
                                                    <div style={{ background: '#3b82f6', color: '#fff', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', fontSize: '10px' }}>
                                                        {kData?.code || 'K'}
                                                    </div>
                                                    <span>{kData?.name || 'Kolonne'}</span>
                                                    {isConflictDetectionEnabled && kolonneShared && kolonneShared.size > 0 && (
                                                        <span style={{ marginLeft: '4px', color: '#ef4444' }}>⚠️</span>
                                                    )}
                                                </div>
                                            {!isReadOnly && (
                                                <button 
                                                    type="button"
                                                    className="res-remove" 
                                                    onClick={(e) => { e.stopPropagation(); removeKolonne(kId); }}
                                                    style={{ position: 'static', transform: 'none', background: 'transparent', color: '#ef4444', opacity: 0.6, padding: '2px', fontSize: '12px' }}
                                                    title="Kolonne entfernen"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                        <div style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {grouped[kId].workers.map(w => {
                                                const allConflicts = liveConflicts.workers[String(w.worker_id)] || [];
                                                const shared = kolonneShared || new Set();
                                                const individual = allConflicts.filter(msg => !shared.has(msg));
                                                
                                                return (
                                                    <div 
                                                        key={w.worker_id} 
                                                        className="resource-chip"
                                                        style={{ cursor: (isConflictDetectionEnabled && individual.length > 0) ? 'pointer' : 'default' }}
                                                        onMouseEnter={(e) => isConflictDetectionEnabled && individual.length > 0 && handleConflictEnter(e, individual, w.name)}
                                                        onMouseLeave={handleConflictLeave}
                                                    >
                                                        <div className="res-avatar" style={{ background: getResourceColor(w) }}>{getInitials(w.name)}</div>
                                                        <span className="res-name">
                                                            {w.name} 
                                                            {isConflictDetectionEnabled && individual.length > 0 && (
                                                                <span style={{ marginLeft: '4px', color: '#f59e0b' }}>⚠️</span>
                                                            )}
                                                        </span>
                                                        {!isReadOnly && (
                                                            <button type="button" className="res-remove" onClick={(e) => { e.stopPropagation(); removeWorker(w.worker_id); }}>✕</button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {grouped[kId].vehicles.map(fv => {
                                                const v = (vehicles || []).find(vh => String(vh.id) === String(fv.vehicle_id));
                                                const allConflicts = liveConflicts.vehicles[String(fv.vehicle_id)] || [];
                                                const shared = kolonneShared || new Set();
                                                const individual = allConflicts.filter(msg => !shared.has(msg));

                                                return (
                                                    <div 
                                                        key={fv.vehicle_id} 
                                                        className="resource-chip vehicle"
                                                        style={{ cursor: (isConflictDetectionEnabled && individual.length > 0) ? 'pointer' : 'default' }}
                                                        onMouseEnter={(e) => isConflictDetectionEnabled && individual.length > 0 && handleConflictEnter(e, individual, v?.license_plate || v?.internal_name || 'Fahrzeug')}
                                                        onMouseLeave={handleConflictLeave}
                                                    >
                                                        <span>
                                                            🚛 {v?.license_plate || v?.internal_name || 'FHZ'}
                                                            {isConflictDetectionEnabled && individual.length > 0 && (
                                                                <span style={{ marginLeft: '4px', color: '#f59e0b' }}>⚠️</span>
                                                            )}
                                                        </span>
                                                        {!isReadOnly && (
                                                            <button type="button" className="res-remove" onClick={(e) => { e.stopPropagation(); toggleVehicle(fv.vehicle_id); }}>✕</button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Independent Items */}
                            {(independentWorkers.length > 0 || independentVehicles.length > 0) && (
                                <div className="independent-resources" style={{ marginTop: '16px' }}>
                                    <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', paddingLeft: '4px' }}>
                                        Einzelressourcen
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {independentWorkers.map(w => (
                                            <div 
                                                key={w.worker_id} 
                                                className="resource-chip"
                                                style={{ cursor: (isConflictDetectionEnabled && liveConflicts.workers[String(w.worker_id)]) ? 'pointer' : 'default' }}
                                                onMouseEnter={(e) => isConflictDetectionEnabled && liveConflicts.workers[String(w.worker_id)] && handleConflictEnter(e, liveConflicts.workers[String(w.worker_id)], w.name)}
                                                onMouseLeave={handleConflictLeave}
                                            >
                                                <div className="res-avatar" style={{ background: getResourceColor(w) }}>{getInitials(w.name)}</div>
                                                <span className="res-name">
                                                    {w.name} 
                                                    {isConflictDetectionEnabled && liveConflicts.workers[String(w.worker_id)] && (
                                                        <span style={{ marginLeft: '4px', color: '#f59e0b' }}>⚠️</span>
                                                    )}
                                                </span>
                                                {!isReadOnly && (
                                                    <button type="button" className="res-remove" onClick={(e) => { e.stopPropagation(); removeWorker(w.worker_id); }}>✕</button>
                                                )}
                                            </div>
                                        ))}
                                        {independentVehicles.map(fv => {
                                            const v = (vehicles || []).find(vh => String(vh.id) === String(fv.vehicle_id));
                                            return (
                                                <div 
                                                    key={fv.vehicle_id} 
                                                    className="resource-chip vehicle"
                                                    style={{ cursor: (isConflictDetectionEnabled && liveConflicts.vehicles[String(fv.vehicle_id)]) ? 'pointer' : 'default' }}
                                                    onMouseEnter={(e) => isConflictDetectionEnabled && liveConflicts.vehicles[String(fv.vehicle_id)] && handleConflictEnter(e, liveConflicts.vehicles[String(fv.vehicle_id)], v?.license_plate || v?.internal_name || 'Fahrzeug')}
                                                    onMouseLeave={handleConflictLeave}
                                                >
                                                    <span>
                                                        🚛 {v?.license_plate || v?.internal_name || 'FHZ'}
                                                        {isConflictDetectionEnabled && liveConflicts.vehicles[String(fv.vehicle_id)] && (
                                                            <span style={{ marginLeft: '4px', color: '#f59e0b' }}>⚠️</span>
                                                        )}
                                                    </span>
                                                    {!isReadOnly && (
                                                        <button type="button" className="res-remove" onClick={(e) => { e.stopPropagation(); toggleVehicle(fv.vehicle_id); }}>✕</button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResourceAllocation;
